/**
 * useOverlayGate — hook voor auto-verschijnende pop-ups.
 *
 * Voorbeeld:
 *   const gate = useOverlayGate('feedback', 20);
 *   useEffect(() => {
 *     if (readyToShow) gate.request();
 *     return () => gate.release();
 *   }, [readyToShow]);
 *   if (!gate.canShow) return null;
 *
 * Zolang `canShow` false blijft, wacht deze overlay netjes op zijn beurt.
 * `release()` moet altijd worden aangeroepen bij unmount OF wanneer de
 * gebruiker de overlay sluit, zodat de volgende in de rij groen licht krijgt.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { requestOverlay, releaseOverlay } from '../lib/overlayQueue';

export function useOverlayGate(name, priority) {
  const [canShow, setCanShow] = useState(false);
  const requestedRef = useRef(false);

  const request = useCallback(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;
    requestOverlay(name, priority, () => setCanShow(true));
  }, [name, priority]);

  const release = useCallback(() => {
    releaseOverlay(name);
    setCanShow(false);
    requestedRef.current = false;
  }, [name]);

  // Auto-release bij unmount
  useEffect(() => {
    return () => {
      if (requestedRef.current) releaseOverlay(name);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { canShow, request, release };
}

export default useOverlayGate;
